import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { GuidedTour } from "@/components/GuidedTour";
import { Loader2 } from "lucide-react";
import { applySkin } from "@/lib/skin";

applySkin();

import Login from "@/pages/Login";
import Home from "@/pages/Home";
import AuthCallback from "@/pages/AuthCallback";
import MemberDashboard from "@/pages/MemberDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SundayNewsForm from "@/pages/SundayNewsForm";
import CommsRequestForm from "@/pages/CommsRequestForm";
import MySubmissions from "@/pages/MySubmissions";
import SubmissionDetail from "@/pages/SubmissionDetail";
import CalendarPage from "@/pages/CalendarPage";
import Guidelines from "@/pages/Guidelines";
import Help from "@/pages/Help";
import Profile from "@/pages/Profile";
import Users from "@/pages/Users";
import ResetPassword from "@/pages/ResetPassword";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import IssueBuilder from "@/pages/IssueBuilder";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ProcessPoster from "@/pages/ProcessPoster";

const LEADERSHIP = ["president", "vice_president"];
const STAFF = ["comms_admin", "technology", "comms_team", "pr_chairman", "president", "vice_president"];
const ADMIN_LEADERSHIP = ["comms_admin", "technology", "president", "vice_president"];
const homeFor = (role) => (STAFF.includes(role) ? "/admin" : "/dashboard");

const FullLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function Protected({ children, roles, feature }) {
  const { user, loading, features } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  if (feature && features && features[feature] === false) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  if (user.must_change_password) return <ForcePasswordChange />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/home" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function AegoLogin() {
  localStorage.setItem("cc_skin", "aka");
  document.documentElement.setAttribute("data-skin", "aka");
  return <Login />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <GuidedTour>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/AEGO" element={<AegoLogin />} />
      <Route path="/aego" element={<AegoLogin />} />
      <Route path="/home" element={<Home />} />
      <Route path="/demo" element={<Home />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/process-flow" element={<ProcessPoster />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/dashboard" element={<Protected roles={["member"]}><MemberDashboard /></Protected>} />
      <Route path="/admin" element={<Protected roles={STAFF}><AdminDashboard /></Protected>} />
      <Route path="/submit/sunday-news" element={<Protected feature="publication_builder"><SundayNewsForm /></Protected>} />
      <Route path="/submit/sunday-news/:id" element={<Protected feature="publication_builder"><SundayNewsForm /></Protected>} />
      <Route path="/submit/comms-request" element={<Protected><CommsRequestForm /></Protected>} />
      <Route path="/submit/comms-request/:id" element={<Protected><CommsRequestForm /></Protected>} />
      <Route path="/submissions" element={<Protected><MySubmissions /></Protected>} />
      <Route path="/submissions/:id" element={<Protected><SubmissionDetail /></Protected>} />
      <Route path="/calendar" element={<Protected feature="calendar"><CalendarPage /></Protected>} />
      <Route path="/guidelines" element={<Protected><Guidelines /></Protected>} />
      <Route path="/help" element={<Protected><Help /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/users" element={<Protected roles={ADMIN_LEADERSHIP}><Users /></Protected>} />
      <Route path="/issues" element={<Protected roles={["comms_admin", "technology", "comms_team", "pr_chairman"]} feature="publication_builder"><IssueBuilder /></Protected>} />
      <Route path="/reports" element={<Protected roles={STAFF} feature="advanced_reports"><Reports /></Protected>} />
      <Route path="/settings" element={<Protected roles={ADMIN_LEADERSHIP}><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </GuidedTour>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
